import { useState, useEffect } from 'react';
import { Trash2, Loader2, GripVertical } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Modal } from '@/components/modal';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface SavedPrompt {
  text: string;
  chat_mode: string;
}

interface ManagePromptsModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  prompts: SavedPrompt[];
  onDelete: (index: number) => Promise<void>;
  onReorder: (prompts: SavedPrompt[]) => Promise<void>;
}

interface SortablePromptItemProps {
  id: string;
  prompt: SavedPrompt & { _uniqueId: string };
  index: number;
  onDelete: () => Promise<void>;
  isDeleting: boolean;
}

const SortablePromptItem = ({
  id,
  prompt,
  onDelete,
  isDeleting,
}: SortablePromptItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className='flex items-center gap-2 p-2 border rounded bg-background'
    >
      <div
        {...attributes}
        {...listeners}
        className='cursor-grab active:cursor-grabbing touch-none'
        aria-label='Drag to reorder prompt'
      >
        <GripVertical className='size-4 text-muted-foreground' />
      </div>

      <p className='flex-1 truncate' title={prompt.text}>
        {prompt.text}
      </p>

      <Button
        variant='ghost'
        size='sm'
        className='h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10'
        onClick={onDelete}
        disabled={isDeleting}
        aria-label={`Remove prompt: ${prompt.text}`}
      >
        {isDeleting ? (
          <Loader2 className='h-3 w-3 animate-spin' />
        ) : (
          <Trash2 className='h-3 w-3' />
        )}
      </Button>
    </div>
  );
};

type PromptWithId = SavedPrompt & { _uniqueId: string };

export const ManagePromptsModal = ({
  isOpen,
  onOpenChange,
  prompts,
  onDelete,
  onReorder,
}: ManagePromptsModalProps) => {
  // Store prompts with stable unique IDs based on original index
  const [localPrompts, setLocalPrompts] = useState<PromptWithId[]>(() =>
    prompts.map((p, i) => ({ ...p, _uniqueId: `prompt-${i}` })),
  );
  const [isDeleting, setIsDeleting] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  useEffect(() => {
    if (isOpen) {
      setLocalPrompts(
        prompts.map((p, i) => ({ ...p, _uniqueId: `prompt-${i}` })),
      );
    }
  }, [isOpen, prompts]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setLocalPrompts((items) => {
        const oldIndex = items.findIndex((p) => p._uniqueId === active.id);
        const newIndex = items.findIndex((p) => p._uniqueId === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Remove the _uniqueId before sending to parent
      const cleanedPrompts = localPrompts.map(({ _uniqueId, ...rest }) => rest);
      await onReorder(cleanedPrompts as SavedPrompt[]);
      onOpenChange(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setLocalPrompts(
      prompts.map((p, i) => ({ ...p, _uniqueId: `prompt-${i}` })),
    );
    onOpenChange(false);
  };

  return (
    <Modal
      isDialogOpen={isOpen}
      setIsDialogOpen={onOpenChange}
      title='Manage Prompts'
      description={
        <Badge variant='secondary' className='text-xs'>
          {localPrompts.length} / 10
        </Badge>
      }
      footer={
        <div className='flex justify-end gap-2 w-full'>
          <Button variant='outline' size='sm' onClick={handleCancel}>
            Cancel
          </Button>
          <Button size='sm' onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className='h-4 w-4 animate-spin mr-1' />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      }
    >
      <div className='space-y-2 px-4'>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={localPrompts.map((p) => p._uniqueId)}
            strategy={verticalListSortingStrategy}
          >
            {localPrompts.map((prompt, index) => {
              // Extract original index from _uniqueId (format: "prompt-{originalIndex}")
              const originalIndex = parseInt(
                prompt._uniqueId.split('-')[1],
                10,
              );

              return (
                <SortablePromptItem
                  key={prompt._uniqueId}
                  id={prompt._uniqueId}
                  prompt={prompt}
                  index={index}
                  onDelete={async () => {
                    setIsDeleting(index);
                    try {
                      // Use original index for parent's onDelete callback
                      await onDelete(originalIndex);
                      // Remove from local state using current index
                      setLocalPrompts((prev) =>
                        prev.filter((_, i) => i !== index),
                      );
                    } finally {
                      setIsDeleting(null);
                    }
                  }}
                  isDeleting={isDeleting === index}
                />
              );
            })}
          </SortableContext>
        </DndContext>

        {localPrompts.length === 0 && (
          <p className='text-sm text-muted-foreground text-center py-4'>
            No prompts saved yet
          </p>
        )}
      </div>
    </Modal>
  );
};
